import React, { useEffect, useState } from 'react';
import { Plus, Edit3, Trash2, X, ArrowUp, ArrowDown } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Card';
import { Input, Textarea, Field } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { errorMessage } from '@/lib/utils';

// Home-screen feed — super_admin publishes ads, offer promos and facility
// announcements ONCE and they show up on both web and mobile home
// screens (see app.routers.feed). Platform-wide only, unlike offers.
const TYPE_LABEL = { ad: 'Ad', offer: 'Offer promo', facility: 'Facility', announcement: 'Announcement' };
const empty = { title: '', description: '', image_url: '', link_url: '', type: 'announcement', active: true, order: 0 };

export default function AdminFeed() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const { toast } = useToast();

  const load = () => api.get('/admin/feed').then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...empty, order: rows.length }); setEditing(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...empty, ...p }); setEditing(p.id); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, order: Number(form.order) || 0, image_url: form.image_url || null, link_url: form.link_url || null };
      if (editing) await api.put(`/admin/feed/${editing}`, payload);
      else await api.post('/admin/feed', payload);
      toast({ title: editing ? 'Post updated' : 'Post published', variant: 'success' });
      setOpen(false); load();
    } catch (e) { toast({ title: 'Save failed', description: errorMessage(e.response?.data?.detail), variant: 'error' }); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this feed post?')) return;
    try { await api.delete(`/admin/feed/${id}`); toast({ title: 'Post deleted', variant: 'success' }); load(); }
    catch (e) { toast({ title: 'Delete failed', description: errorMessage(e.response?.data?.detail), variant: 'error' }); }
  };

  const reorder = async (row, delta) => {
    try { await api.put(`/admin/feed/${row.id}`, { ...row, order: (row.order || 0) + delta }); load(); }
    catch { /* no-op */ }
  };

  return (
    <div className="space-y-6" data-testid="admin-feed">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-2">Home Screen</div>
          <h1 className="font-heading font-black text-4xl uppercase tracking-tight">Feed</h1>
          <p className="text-sm text-white/50 mt-2 max-w-xl">
            Publish ads, offer promos and facility announcements here — they appear on the home screen of both the website and the mobile app, platform-wide.
          </p>
        </div>
        <Button onClick={openNew} data-testid="add-feed-button"><Plus size={14} /> Add Post</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((p) => (
          <div key={p.id} className="bg-surface border border-white/10 p-6 flex flex-col gap-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Badge tone={p.active ? 'success' : 'default'}>{p.active ? 'Live' : 'Hidden'}</Badge>
              <Badge tone="volt">{TYPE_LABEL[p.type] || p.type}</Badge>
            </div>
            {p.image_url && (
              <div className="aspect-video bg-obsidian overflow-hidden">
                <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />
              </div>
            )}
            <h3 className="font-heading font-black text-xl uppercase">{p.title}</h3>
            <p className="text-sm text-white/50 line-clamp-2 flex-1">{p.description || '—'}</p>
            <div className="flex items-center justify-between pt-2 border-t border-white/10">
              <div className="flex gap-1">
                <Button variant="outline" size="sm" onClick={() => reorder(p, -1)} title="Move up"><ArrowUp size={12} /></Button>
                <Button variant="outline" size="sm" onClick={() => reorder(p, 1)} title="Move down"><ArrowDown size={12} /></Button>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => openEdit(p)} data-testid={`edit-feed-${p.id}`}><Edit3 size={12} /> Edit</Button>
                <Button variant="danger" size="sm" onClick={() => remove(p.id)} data-testid={`delete-feed-${p.id}`}><Trash2 size={12} /></Button>
              </div>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="text-white/40 text-sm col-span-full text-center py-12">No feed posts yet.</div>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-surface border border-white/10 max-w-lg w-full p-6 sm:p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="feed-form-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-bold text-2xl uppercase">{editing ? 'Edit' : 'New'} Post</h2>
              <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <Field label="Type">
                <select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="h-12 w-full border border-white/10 bg-obsidian px-4 text-sm text-white outline-none"
                  data-testid="feed-type"
                >
                  <option value="announcement">Announcement</option>
                  <option value="ad">Ad</option>
                  <option value="offer">Offer promo</option>
                  <option value="facility">Facility</option>
                </select>
              </Field>
              <Field label="Title"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="New Pickleball Courts Open!" data-testid="feed-title" /></Field>
              <Field label="Description"><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Shown under the title on the home screen." /></Field>
              <Field label="Image URL (optional)"><Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://…" data-testid="feed-image" /></Field>
              <Field label="Link URL (optional)" hint="Where tapping the post takes the customer, e.g. a venue or offers page.">
                <Input value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} placeholder="/app/venues" data-testid="feed-link" />
              </Field>
              <Field label="Display order" hint="Lower numbers show first."><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="feed-order" /></Field>
              <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-volt" /> Live on home screen
              </label>
            </div>
            <div className="flex gap-3 mt-8">
              <Button variant="outline" onClick={() => setOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={save} className="flex-1" data-testid="save-feed">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
